<template>
  <section class="signature-project" :style="{ '--views-overflow': `${viewsOverflow}px` }" :aria-labelledby="`${entry.id}-feature-title`">
    <div class="signature-project-copy">
      <span class="signature-project-kicker">{{ showcase.category }}</span>
      <component :is="entry.externalPage ? 'a' : 'router-link'"
        v-bind="entry.externalPage ? { href: projectHref, target: '_blank', rel: 'noopener' } : { to: entry.route }"
        class="signature-project-destination" :aria-label="`Explore ${showcase.title}`">
        <h3 :id="`${entry.id}-feature-title`"><span>{{ showcase.title }}</span><ArrowUpRightIcon class="signature-project-title-arrow" /></h3>
        <p><ArchiveTermText v-if="entry.id === 'searchstory'" :text="showcase.description" /><template v-else>{{ showcase.description }}</template></p>
        <span class="signature-project-cta"><span>Explore the project</span></span>
      </component>
      <div class="signature-project-links">
        <CathayFlexDeck v-if="entry.id === 'cathayflex'" />
        <GitHubRepoLink v-if="entry.id === 'slidepoise'" href="https://github.com/henryhyw/slidepoise" label="Source on GitHub" compact />
        <button v-if="entry.id === 'searchstory'" type="button" class="resource-action compose-view-entry" @click="openView"><span>Compose a <AiViewTerm /></span></button>
        <GalleryEntryLink v-if="entry.id === 'searchstory'" />
        <ProjectArchitecture v-else-if="entry.id === 'slidepoise'" :project-id="entry.id" />
      </div>
      <div v-if="wide" class="signature-project-meta">
        <span class="signature-project-period">{{ entry.period }}</span>
        <SourceViewLinks :source-id="entry.sourceId" anchored @reveal-bottom="reserveViewSpace" />
      </div>
    </div>
    <ProjectShowcase :project-id="entry.id" :show-caption="false" :show-controls="false" />
    <div v-if="!wide" class="signature-project-meta signature-project-small-meta">
      <span class="signature-project-period">{{ entry.period }}</span>
      <SourceViewLinks :source-id="entry.sourceId" anchored @reveal-bottom="reserveViewSpace" />
    </div>
  </section>
</template>

<script>
import { projectShowcases } from '../../data/projectShowcases';
import { projectWebsiteTarget } from '../../utils/viewSourceTarget';
import ProjectShowcase from './ProjectShowcase.vue';
import ProjectArchitecture from './ProjectArchitecture.vue';
import CathayFlexDeck from './CathayFlexDeck.vue';
import GalleryEntryLink from './GalleryEntryLink.vue';
import GitHubRepoLink from './GitHubRepoLink.vue';
import SourceViewLinks from './SourceViewLinks.vue';
import AiViewTerm from './AiViewTerm.vue';
import ArchiveTermText from './ArchiveTermText.vue';
import '../../assets/css/resource-action.css';

export default {
  name: 'SignatureProject',
  components: { ProjectShowcase, ProjectArchitecture, CathayFlexDeck, GalleryEntryLink, GitHubRepoLink, SourceViewLinks, AiViewTerm, ArchiveTermText },
  props: { entry: { type: Object, required: true } },
  data() { return { viewsOverflow: 0, wide: typeof window !== 'undefined' && window.innerWidth > 960 }; },
  mounted() {
    this._wideQuery = window.matchMedia('(min-width: 961px)');
    this._onWideChange = () => { this.wide = this._wideQuery.matches; this.viewsOverflow = 0; };
    this._wideQuery.addEventListener('change', this._onWideChange);
  },
  beforeUnmount() { this._wideQuery?.removeEventListener('change', this._onWideChange); },
  computed: {
    showcase() { return projectShowcases[this.entry.id]; },
    projectHref() { return projectWebsiteTarget(this.entry.route)?.href || this.entry.route; },
  },
  methods: {
    openView() { window.dispatchEvent(new CustomEvent('open-site-view')); },
    reserveViewSpace(bottom) {
      if (bottom == null) { this.viewsOverflow = 0; return; }
      const section = this.$el;
      const contentBottom = section.getBoundingClientRect().bottom - parseFloat(getComputedStyle(section).paddingBottom);
      this.viewsOverflow = Math.max(0, Math.ceil(bottom - contentBottom + 12));
    }
  }
};
</script>

<style>
.signature-project {
  --signature-highlight: rgb(139 122 86 / 13%);
  --signature-hover-ink: #181817;
  display: grid;
  grid-template-columns: minmax(0, .82fr) minmax(0, 2fr);
  gap: 38px;
  align-items: center;
  padding: 42px 0 calc(36px + var(--views-overflow, 0px));
  cursor: var(--site-default-cursor);
}
.signature-project-copy { min-width: 0; padding-bottom: 38px; }
.signature-project .signature-project-kicker { display: block; margin-bottom: 17px; font: 11px/1.5 'Quattrocento Sans', sans-serif; letter-spacing: .11em; text-transform: uppercase; color: var(--site-text-tertiary); }
.signature-project .signature-project-destination { display: block; color: inherit; text-decoration: none; background: none !important; border: 0; cursor: var(--site-interactive-cursor); }
.signature-project-destination:focus-visible { outline: 2px solid currentColor; outline-offset: 7px; }
.signature-project h3 { display: flex; align-items: baseline; gap: 9px; margin: 0 0 20px; font: 700 32px/1.12 'PT Serif', Georgia, serif; letter-spacing: -.035em; color: #30302e; }
.signature-project .signature-project-title-arrow { width: 19px; height: 19px; flex: 0 0 19px; color: var(--site-text-tertiary); transition: transform 180ms cubic-bezier(.23, 1, .32, 1), color 180ms ease; }
.signature-project .signature-project-destination > p { margin: 0; font: 15px/1.7 'PT Serif', Georgia, serif; color: var(--site-text-secondary); }
.signature-project-cta { display: flex; align-items: center; min-height: 44px; margin-top: 13px; font: 13px/20px 'Quattrocento Sans', sans-serif; }
.signature-project-cta > span { text-decoration: underline; text-decoration-color: rgb(128 128 120 / 45%); text-underline-offset: 5px; text-decoration-thickness: 1px; }
.signature-project-cta > span {
  background-image: linear-gradient(var(--signature-highlight), var(--signature-highlight));
  background-repeat: no-repeat;
  background-position: 0 100%;
  background-size: 100% 0;
  transition: background-size 180ms cubic-bezier(.23, 1, .32, 1), color 180ms ease;
}
.signature-project-links { display: flex; flex-wrap: wrap; align-items: center; gap: 0 20px; margin-top: 0; }
.signature-project-links .resource-action { font-size: 12px; gap: 7px; }
.signature-project-links .resource-action { height: 44px; min-height: 44px; padding-block: 0; line-height: 20px; }
.signature-project-links > .project-architecture { display: flex; align-items: center; }
.signature-project-meta { position: relative; display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 32px; margin-top: 22px; }
.signature-project-period { display: block; flex: 0 0 auto; font: 11px/1.5 'Quattrocento Sans', sans-serif; letter-spacing: .015em; color: var(--site-text-tertiary); }
.signature-project-meta .source-view-links { position: static; min-width: 0; margin: 0; font-size: 12px; }
.signature-project .signature-project-meta .source-view-links.is-anchored { position: static; }
.signature-project-meta .source-view-trigger { gap: 8px; }
.signature-project-meta .source-view-label { gap: 4px; white-space: nowrap; }
.signature-project-meta .source-view-star { font-size: 13px; }
.signature-project-meta .source-view-list { margin-left: 0; font-size: 13px; }
.signature-project-small-meta { width: min(100%, 420px); margin-top: 0; }
body.dark-mode .signature-project { --signature-highlight: rgb(214 195 155 / 16%); --signature-hover-ink: #f5f4ef; }
body.dark-mode .signature-project h3 { color: #e5e4df; }
/* One destination, one quiet response across its entire text area. */
.signature-project-destination h3,
.signature-project .signature-project-destination > p,
.signature-project-cta { transition: color 180ms ease; }
.signature-project-destination * { cursor: inherit; }
.signature-project-destination:focus-visible :is(h3, p, .signature-project-cta) { color: var(--signature-hover-ink); }
.signature-project-destination:focus-visible .signature-project-title-arrow { transform: translate(3px, -3px); color: var(--signature-hover-ink); }
.signature-project-destination:focus-visible .signature-project-cta > span { background-size: 100% .5em; }
@media (hover: hover) and (pointer: fine) {
  .signature-project-destination:hover :is(h3, p, .signature-project-cta) { color: var(--signature-hover-ink); }
  .signature-project-destination:hover .signature-project-title-arrow { transform: translate(3px, -3px); color: var(--signature-hover-ink); }
  .signature-project-destination:hover .signature-project-cta > span { background-size: 100% .5em; }
}
@media (max-width: 960px) {
  .signature-project { grid-template-columns: minmax(0, 1fr); gap: 26px; padding-block: 32px calc(32px + var(--views-overflow, 0px)); }
  .signature-project-copy { padding: 0; }
  .signature-project .signature-project-destination { display: grid; grid-template-columns: minmax(0, .8fr) minmax(0, 1fr); gap: 0 36px; }
  .signature-project h3 { grid-column: 1; grid-row: 1 / 3; margin: 0; }
  .signature-project .signature-project-destination > p { grid-column: 2; }
  .signature-project-cta { grid-column: 2; margin-top: 3px; }
  .signature-project-links { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-start; gap: 0 28px; margin: 6px 0 0 calc((100% - 36px) * 4 / 9 + 36px); }
  .signature-project-meta { margin-top: 12px; }
}
@media (max-width: 600px) {
  .signature-project .signature-project-destination { display: block; }
  .signature-project h3 { margin-bottom: 15px; font-size: 30px; }
  .signature-project .signature-project-destination > p { font-size: 14px; }
  .signature-project-links { margin-left: 0; margin-top: 0; }
}
@media (prefers-reduced-motion: reduce) { .signature-project * { transition: none !important; } }
</style>
